import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, ArrowRight, FileText, Search, RotateCcw, X } from 'lucide-react';
import { matryoshkaSchema, OWN_TEXT_IDEA_CODE } from '@masha/shared';
import type { ContentFormatCode } from '@masha/shared';
import { api, ApiError } from '../lib/api';
import { IdeaCard } from '../components/IdeaCard';
import { HowItWorks } from '../components/HowItWorks';
import { PaywallView } from '../components/PaywallView';
import { HOW_IT_WORKS, carouselNote, reelsNote } from '../lib/howItWorksCopy';
import { usePricing } from '../lib/pricing';
import { Button, Spinner } from '../components/ui';
import { cn } from '../lib/cn';
import { haptic } from '../lib/tma';

function useDebouncedValue<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return debounced;
}

export function IdeasPage() {
  const [searchParams] = useSearchParams();
  const format: ContentFormatCode =
    searchParams.get('format') === 'carousel' ? 'carousel' : 'reels';
  const title = format === 'carousel' ? 'Карусели' : 'Идеи Reels';
  const pricing = usePricing();

  const [q, setQ] = useState('');
  const [matryoshka, setMatryoshka] = useState('');
  const [goalIds, setGoalIds] = useState<Set<number>>(new Set());

  const debouncedQ = useDebouncedValue(q, 300);

  const ideasQuery = useQuery({
    queryKey: [
      'ideas',
      { format, q: debouncedQ, matryoshka, goalIds: Array.from(goalIds).sort().join(',') },
    ],
    queryFn: () =>
      api.listIdeas({
        format_code: format,
        q: debouncedQ || undefined,
        matryoshka: matryoshka || undefined,
        goal_ids: goalIds.size > 0 ? Array.from(goalIds).sort().join(',') : undefined,
        limit: 100,
      }),
  });

  const goalsQuery = useQuery({
    queryKey: ['goals-for-filter'],
    queryFn: () => api.listGoals(),
  });

  const goals = goalsQuery.data?.goals ?? [];

  function toggleGoal(id: number) {
    haptic('light');
    setGoalIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  const hasFilters = q !== '' || matryoshka !== '' || goalIds.size > 0;

  function resetFilters() {
    haptic('light');
    setQ('');
    setMatryoshka('');
    setGoalIds(new Set());
  }

  const { data, isFetching, isError, refetch } = ideasQuery;

  if (ideasQuery.error instanceof ApiError && ideasQuery.error.status === 402) {
    return <PaywallView />;
  }

  return (
    <div className="min-h-screen p-4 max-w-lg mx-auto safe-top safe-bottom pb-12">
      <header className="flex items-center justify-between mb-4">
        <Link
          to="/"
          className="inline-flex items-center gap-1 text-sm text-tg-hint hover:text-tg-text transition-colors"
        >
          <ArrowLeft size={16} />
          Назад
        </Link>
        <h1 className="text-lg font-semibold">{title}</h1>
        <span className="w-12" />
      </header>

      <HowItWorks
        id={format}
        steps={HOW_IT_WORKS[format].steps}
        note={format === 'carousel' ? carouselNote(pricing.carousel) : reelsNote(pricing.reels)}
        className="mb-4"
      />

      {format === 'carousel' && <OwnTextBanner />}

      <div className="space-y-3 mb-4">
        <div className="relative">
          <Search
            size={18}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-tg-hint pointer-events-none"
          />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Поиск идей"
            className="w-full pl-10 pr-3 py-3 rounded-xl bg-tg-section-bg border border-tg-hint/30 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/40 transition-colors"
          />
        </div>

        <select
          value={matryoshka}
          onChange={(e) => setMatryoshka(e.target.value)}
          className="w-full p-3 rounded-xl bg-tg-section-bg border border-tg-hint/30 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/40 transition-colors"
        >
          <option value="">Все матрёшки</option>
          {matryoshkaSchema.options.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>

        {goals.length > 0 && (
          <div>
            <p className="text-xs text-tg-hint mb-2 font-medium uppercase tracking-wider">Цели</p>
            <div className="flex flex-wrap gap-2">
              {goals.map((goal) => {
                const active = goalIds.has(goal.id);
                return (
                  <button
                    key={goal.id}
                    type="button"
                    onClick={() => toggleGoal(goal.id)}
                    className={cn(
                      'px-3 py-1.5 rounded-full text-sm font-medium border transition-[transform,opacity,background-color,color,border-color] duration-150 ease-smooth active:scale-[0.97]',
                      active
                        ? 'bg-brand text-brand-on border-brand'
                        : 'bg-tg-section-bg border-transparent text-tg-text/80 hover:bg-tg-section-bg/80',
                    )}
                  >
                    {goal.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {hasFilters && (
          <button
            type="button"
            onClick={resetFilters}
            className="inline-flex items-center gap-1 text-sm text-tg-hint hover:text-tg-text transition-colors"
          >
            <X size={14} />
            Сбросить фильтры
          </button>
        )}
      </div>

      <p className="text-xs text-tg-hint mb-3 tabular-nums">Найдено {data?.total ?? 0}</p>

      {isError && (
        <div className="text-center py-8">
          <p className="text-sm text-tg-hint mb-3">Не удалось загрузить идеи</p>
          <Button onClick={() => refetch()} variant="primary" leftIcon={<RotateCcw size={16} />}>
            Повторить
          </Button>
        </div>
      )}

      {isFetching && !data && (
        <div className="flex flex-col items-center py-8">
          <Spinner />
          <p className="text-sm text-tg-hint mt-3">Загружаем</p>
        </div>
      )}

      {data && data.total === 0 && (
        <p className="text-sm text-tg-hint text-center py-8">Нет идей по этим фильтрам</p>
      )}

      {data && data.ideas.length > 0 && (
        <div className="grid grid-cols-2 gap-3">
          {data.ideas.map((idea) => (
            <IdeaCard key={idea.id} idea={idea} />
          ))}
        </div>
      )}
    </div>
  );
}

/** Iter 20: the only entry to the hidden own-text carousel idea. */
function OwnTextBanner() {
  return (
    <Link
      to={`/sessions/new?idea_code=${OWN_TEXT_IDEA_CODE}`}
      onClick={() => haptic('light')}
      className="flex items-center gap-3 w-full px-4 py-3 mb-4 rounded-2xl bg-brand-muted text-tg-text transition-[transform,opacity] duration-150 ease-smooth active:scale-[0.99] active:opacity-80"
    >
      <span className="flex-shrink-0 w-10 h-10 rounded-xl bg-brand text-brand-on flex items-center justify-center">
        <FileText size={20} />
      </span>
      <span className="flex-1 min-w-0">
        <span className="block text-sm font-semibold leading-tight">Есть свой текст?</span>
        <span className="block text-xs text-tg-hint leading-snug mt-0.5">
          Сделай из него карусель, без вопросов
        </span>
      </span>
      <ArrowRight size={16} className="text-tg-hint flex-shrink-0" />
    </Link>
  );
}
